/*
 * Ad-landing coupons: a label, never a discount.
 *
 * An ad links to /brand/<slug>?coupon=CODE (captured by linkCoupon.js). The
 * visitor pays exactly the listing price everyone pays; the store just shows
 * that price as the brand's MRP with "CODE applied" taking off the gap. What
 * is charged never changes, and no backend coupon is involved.
 *
 * The server says which codes exist and which plans' MRPs the team has checked
 * against the brand's own price (config/app_update?os=web -> ad_coupons; the
 * ConfigData row is described in the backend's configuration/ad_coupons.py).
 * A plan that isn't verified shows no coupon: a saving measured from a price
 * nobody charges would be a false discount. Rupee plans only.
 */

import { useEffect, useState } from "react";
import Api from "./Api/Api";
import { getLinkCoupon } from "./linkCoupon";

const EMPTY = { codes: [], verified_plan_ids: [] };
let pending = null;

/** The server's ad_coupons, asked once per page load. Fails closed. */
export const fetchAdCoupons = () => {
  if (!pending) {
    pending = Promise.resolve()
      .then(() => Api.GetAppUpdate())
      .then((res) => {
        if (!res || res.isAxiosError || res instanceof Error) {
          pending = null;
          return EMPTY;
        }
        const ad = res.data?.ad_coupons;
        return {
          codes: Array.isArray(ad?.codes) ? ad.codes.map((c) => String(c).toUpperCase()) : [],
          verified_plan_ids: Array.isArray(ad?.verified_plan_ids) ? ad.verified_plan_ids.map(Number) : [],
        };
      })
      .catch(() => {
        pending = null;
        return EMPTY;
      });
  }
  return pending;
};

/** For tests. */
export const resetAdCoupons = () => {
  pending = null;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * { code, saving } when this plan shows the ad coupon, else null.
 * saving = MRP - listing price, so MRP - saving is exactly what is charged.
 */
export const adCouponFor = ({ config, code, planId, mrp, price, currency }) => {
  if (!config || !code) return null;
  if (String(currency || "inr").toLowerCase() !== "inr") return null;
  if (!config.codes.includes(String(code).toUpperCase())) return null;
  if (!config.verified_plan_ids.includes(Number(planId))) return null;
  const saving = Math.round(num(mrp) - num(price));
  if (!(saving > 0)) return null;
  return { code: String(code).toUpperCase(), saving };
};

/** The server config plus this browser's ad code; null until both are known. */
export const useAdCoupons = () => {
  const [state, setState] = useState(null);
  useEffect(() => {
    const link = getLinkCoupon();
    if (!link) return undefined;
    let live = true;
    fetchAdCoupons().then((config) => {
      if (live) setState({ config, code: link.code });
    });
    return () => {
      live = false;
    };
  }, []);
  return state;
};
